import { useMemo } from 'react';
import { Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  ArchiveRounded as ArchiveIcon,
  CheckCircleRounded as CheckCircleIcon,
  CancelRounded as CancelIcon,
} from '@material-ui/icons';

type StatusChipProps = {
  isActive: boolean;
  archived?: boolean;
  inactiveLabel?: string;
  size?: 'small' | 'medium';
};

const useStyles = makeStyles(() => ({
  root: (props: { backgroundColor: string; color: string }) => ({
    borderRadius: 4,
    textTransform: 'capitalize',
    backgroundColor: props.backgroundColor,
    color: props.color,

    '& .MuiChip-label': {
      display: 'flex',
      alignItems: 'center',
      gap: 6,

      '& > svg': {
        width: 16,
        height: 16,
        fill: props.color,
      },

      '& > span': {
        fontSize: 12,
        lineHeight: '14px',
      },
    },
  }),
}));

type ChipStyle = { label: string; backgroundColor: string; color: string; icon: React.ReactElement };

const getChipStyle = (isActive: boolean, archived: boolean, inactiveLabel: string): ChipStyle => {
  if (archived) {
    return { label: 'Archivé', backgroundColor: '#9e9e9e', color: '#ffffff', icon: <ArchiveIcon /> };
  }
  if (isActive) {
    return { label: 'Actif', backgroundColor: '#4caf50', color: '#ffffff', icon: <CheckCircleIcon /> };
  }
  return { label: inactiveLabel, backgroundColor: '#9e9e9e', color: '#ffffff', icon: <CancelIcon /> };
};

export default function StatusChip({
  isActive,
  archived = false,
  inactiveLabel = 'Inactif',
  size = 'small',
}: StatusChipProps) {
  const { label, backgroundColor, color, icon } = useMemo(
    () => getChipStyle(isActive, archived, inactiveLabel),
    [isActive, archived, inactiveLabel]
  );

  const classes = useStyles({ backgroundColor, color });

  return (
    <Chip
      className={classes.root}
      size={size}
      label={
        <>
          {icon}
          <span>{label}</span>
        </>
      }
    />
  );
}
